import { useCallback } from 'react';

import i18next from 'i18next';
import { Trans, TransProps, initReactI18next, useTranslation as useTranslationDefault } from 'react-i18next';

import { formatNumber, formatPercent } from '@/helpers/number';
import useLocalStorage from '@/hooks/useLocalStorage';

import de from './de';
import en from './en';
import es from './es';
import es_419 from './es-419';
import es_MX from './es-MX';
import fr from './fr';
import fr_CA from './fr-CA';
import it from './it';
import nl from './nl';
import pl from './pl';
import pt from './pt';

const DEFAULT_LANGUAGE = 'en';

export const resources = {
  en,
  es,
  fr,
  it,
  nl,
  pl,
  pt,
  de,
  'es-419': es_419,
  'es-MX': es_MX,
  'fr-CA': fr_CA,
} as const;

const AVAILABLE_LANGUAGES = {
  en: 'English',
  es: 'Español',
  fr: 'Français',
  it: 'Italiano',
  nl: 'Nederlands',
  pl: 'Polski',
  pt: 'Português',
  de: 'Deutsch',
  'es-419': 'Español (Latinoamérica)',
  'es-MX': 'Español (México)',
  'fr-CA': 'Français (Canada)',
};

let i18nInstance = i18next;
let initialized = false;

function validateLanguage(language: string) {
  if (Object.keys(AVAILABLE_LANGUAGES).includes(language)) {
    return language;
  }

  return DEFAULT_LANGUAGE;
}

function detectLanguage() {
  if (typeof window === 'undefined') {
    return DEFAULT_LANGUAGE;
  }

  return window.navigator.language.split('-')[0];
}

function useTranslation(...args: Parameters<typeof useTranslationDefault>) {
  const { t, ...rest } = useTranslationDefault(...args);

  const tComponent = (
    key: string,
    components?: TransProps<any>['components'],
    options?: string | { count?: number; context?: string; defaults?: string; [x: string]: any },
  ) => {
    const props: TransProps<any> = {
      i18nKey: key,
      t,
      components,
    };

    if (typeof options === 'string') {
      props['defaults'] = options;
    } else {
      const { count, defaults, ...rest } = options || {};

      props['count'] = count;
      props['defaults'] = defaults;
      props['values'] = rest;
    }

    return <Trans {...props} />;
  };

  return { t, tComponent, ...rest };
}

function useI18n() {
  const [language, setLanguage] = useLocalStorage('language', detectLanguage);

  const changeLanguage: typeof i18next.changeLanguage = useCallback(
    (lang) => i18nInstance.changeLanguage(lang, () => setLanguage(lang)),
    [setLanguage],
  );

  const getCurrentLanguage = () => (i18nInstance.language || DEFAULT_LANGUAGE) as keyof typeof AVAILABLE_LANGUAGES;

  const init = () => {
    if (initialized) {
      return;
    }

    i18nInstance = i18next.createInstance();

    i18nInstance.use(initReactI18next).init({
      lng: validateLanguage(language),
      fallbackLng: DEFAULT_LANGUAGE,
      defaultNS: DEFAULT_LANGUAGE,
      ns: ['es', 'en'],
      resources,
      interpolation: {
        escapeValue: false,
      },
    });

    i18nInstance.services.formatter?.add('capitalize', (value: string) => value.charAt(0).toUpperCase() + value.slice(1));
    i18nInstance.services.formatter?.add('lowercase', (value: string) => value.toLowerCase());
    i18nInstance.services.formatter?.add('uppercase', (value: string) => value.toUpperCase());
    i18nInstance.services.formatter?.add('percent', (value: number) => formatPercent(value));
    i18nInstance.services.formatter?.add('number', (value: number) => formatNumber(value));

    initialized = true;

    return i18nInstance;
  };

  return {
    changeLanguage,
    getCurrentLanguage,
    init,
  };
}

export { AVAILABLE_LANGUAGES, DEFAULT_LANGUAGE, useI18n, useTranslation, i18nInstance as i18n };
